import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';

const roundMoney = (value = 0) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const QUARTER_DEFS = [
    { key: 'Q1', label: 'Q1 (Gen–Mar)', months: [1, 2, 3] },
    { key: 'Q2', label: 'Q2 (Apr–Giu)', months: [4, 5, 6] },
    { key: 'Q3', label: 'Q3 (Lug–Set)', months: [7, 8, 9] },
    { key: 'Q4', label: 'Q4 (Ott–Dic)', months: [10, 11, 12] },
];

export const getQuarterFromMonth = (month) => {
    if (month <= 3) return 'Q1';
    if (month <= 6) return 'Q2';
    if (month <= 9) return 'Q3';
    return 'Q4';
};

export const getQuarterMonths = (quarter) => {
    const def = QUARTER_DEFS.find((q) => q.key === quarter);
    return def ? def.months : [];
};

export const isDateInQuarter = (dateStr, quarter, year) => {
    if (!dateStr) return false;
    const d = dayjs(dateStr);
    if (!d.isValid()) return false;
    const months = getQuarterMonths(quarter);
    return d.year() === Number(year) && months.includes(d.month() + 1);
};

export const getCurrentQuarter = () => getQuarterFromMonth(dayjs().month() + 1);

export const getPackageStatusLabel = (status) => {
    const labels = {
        bozza: 'Bozza',
        pronto: 'Pronto',
        inviato: 'Inviato al commercialista',
    };
    return labels[status] || status;
};

export const getPackageStatusColor = (status) => {
    const colors = {
        bozza: 'default',
        pronto: 'warning',
        inviato: 'success',
    };
    return colors[status] || 'default';
};

export const getVatEntryTypeLabel = (tipo) => {
    const labels = {
        fattura_acquisto: 'Fattura acquisto',
        fattura_b2b: 'Fattura B2B',
        fattura_libera: 'Fattura libera',
        corrispettivo: 'Corrispettivo',
        fattura_pa: 'Fattura PA',
    };
    return labels[tipo] || tipo;
};

export const buildRegistroAcquistiEntries = (passiveWorkspace, { quarter, year, companyDocumentId }) => {
    const invoices = Object.values(passiveWorkspace?.invoices || {});
    return invoices
        .filter((inv) => {
            if (companyDocumentId && inv.companyDocumentId !== companyDocumentId) return false;
            if (!inv.protocolNumber) return false;
            if (!isDateInQuarter(inv.issueDate, quarter, year)) return false;
            return true;
        })
        .flatMap((inv) => {
            const net = roundMoney(inv.computed?.totals?.net ?? inv.xmlTotals?.net ?? 0);
            const vat = roundMoney(inv.computed?.totals?.vat ?? inv.xmlTotals?.vat ?? 0);
            if (!net && !vat) return [];
            const vatRate = net > 0 ? Math.round((vat / net) * 100) : 22;
            return [{
                id: `acq-${inv.id}`,
                date: inv.issueDate,
                numero: `Reg.${inv.protocolRegister || 1}/${inv.protocolNumber}`,
                fornitore: inv.xmlSupplierName || 'Fornitore',
                descrizione: inv.supplierReferenceNumber
                    ? `Fattura n. ${inv.supplierReferenceNumber}`
                    : inv.id,
                imponibile: net,
                aliquota: vatRate,
                iva: vat,
                totale: roundMoney(net + vat),
                tipo: 'fattura_acquisto',
            }];
        })
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
};

export const buildRegistroVenditeEntries = (billingWorkspace, activeWorkspace, complianceWorkspace, { quarter, year, companyDocumentId }) => {
    const entries = [];

    (billingWorkspace?.b2bInvoices || []).forEach((inv) => {
        if (companyDocumentId && inv.companyDocumentId !== companyDocumentId) return;
        if (!['emessa', 'consegnata', 'pagata'].includes(inv.status)) return;
        if (!isDateInQuarter(inv.issueDate, quarter, year)) return;
        const byVat = {};
        (inv.lines || []).forEach((line) => {
            const vr = line.vatRate ?? 22;
            if (!byVat[vr]) byVat[vr] = { net: 0, vat: 0 };
            const lineNet = roundMoney((line.quantity || 0) * (line.unitPrice || 0));
            byVat[vr].net = roundMoney(byVat[vr].net + lineNet);
            byVat[vr].vat = roundMoney(byVat[vr].vat + lineNet * vr / 100);
        });
        Object.entries(byVat).forEach(([vr, totals]) => {
            entries.push({
                id: `vend-b2b-${inv.id}-vat${vr}`,
                date: inv.issueDate,
                numero: inv.referenceNumber || inv.id,
                cliente: inv.recipient?.businessName || 'Cliente B2B',
                descrizione: `Fattura ${inv.referenceNumber || inv.id}`,
                imponibile: totals.net,
                aliquota: Number(vr),
                iva: totals.vat,
                totale: roundMoney(totals.net + totals.vat),
                tipo: 'fattura_b2b',
            });
        });
    });

    (billingWorkspace?.freeInvoices || []).forEach((inv) => {
        if (companyDocumentId && inv.companyDocumentId !== companyDocumentId) return;
        if (!['emessa', 'pagata'].includes(inv.status)) return;
        if (!isDateInQuarter(inv.issueDate, quarter, year)) return;
        const net = roundMoney(inv.netAmount || 0);
        const vat = roundMoney(inv.vatAmount || 0);
        if (!net && !vat) return;
        const vatRate = net > 0 ? Math.round((vat / net) * 100) : 22;
        entries.push({
            id: `vend-free-${inv.id}`,
            date: inv.issueDate,
            numero: inv.referenceNumber || inv.id,
            cliente: inv.recipient?.name || inv.recipient?.businessName || 'Cliente',
            descrizione: `Fattura libera ${inv.referenceNumber || inv.id}`,
            imponibile: net,
            aliquota: vatRate,
            iva: vat,
            totale: roundMoney(net + vat),
            tipo: 'fattura_libera',
        });
    });

    (activeWorkspace?.receipts || []).forEach((receipt) => {
        if (companyDocumentId && receipt.companyDocumentId !== companyDocumentId) return;
        if (receipt.status !== 'emesso') return;
        if (!isDateInQuarter(receipt.date, quarter, year)) return;
        const byVat = {};
        (receipt.lines || []).forEach((line) => {
            const vr = line.vatRate ?? 22;
            if (!byVat[vr]) byVat[vr] = { net: 0, vat: 0 };
            const lineNet = roundMoney((line.quantity || 0) * (line.unitPrice || 0));
            byVat[vr].net = roundMoney(byVat[vr].net + lineNet);
            byVat[vr].vat = roundMoney(byVat[vr].vat + lineNet * vr / 100);
        });
        Object.entries(byVat).forEach(([vr, totals]) => {
            entries.push({
                id: `vend-scontrino-${receipt.id}-vat${vr}`,
                date: receipt.date,
                numero: receipt.receiptNumber || receipt.id,
                cliente: receipt.customerName || 'Cliente privato',
                descrizione: `Corrispettivo ${receipt.receiptNumber || receipt.id}`,
                imponibile: totals.net,
                aliquota: Number(vr),
                iva: totals.vat,
                totale: roundMoney(totals.net + totals.vat),
                tipo: 'corrispettivo',
            });
        });
    });

    (complianceWorkspace?.fatturePA || []).forEach((fattura) => {
        if (companyDocumentId && fattura.companyDocumentId !== companyDocumentId) return;
        if (!['inviata_sdi', 'accettata', 'decorrenza_termini'].includes(fattura.status)) return;
        if (!isDateInQuarter(fattura.issueDate, quarter, year)) return;
        const amount = roundMoney(fattura.amount || 0);
        const vatRate = fattura.vatRate ?? 22;
        const net = roundMoney(amount / (1 + vatRate / 100));
        const vat = roundMoney(amount - net);
        entries.push({
            id: `vend-pa-${fattura.id}`,
            date: fattura.issueDate,
            numero: fattura.documentNumber || fattura.id,
            cliente: fattura.paName || 'Ente pubblico',
            descrizione: `Fattura PA ${fattura.documentNumber || fattura.id}`,
            imponibile: net,
            aliquota: vatRate,
            iva: vat,
            totale: amount,
            tipo: 'fattura_pa',
        });
    });

    return entries.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
};

export const buildSummaryByAliquota = (registroAcquisti, registroVendite) => {
    const aliquoteSet = new Set([
        ...registroAcquisti.map((e) => e.aliquota),
        ...registroVendite.map((e) => e.aliquota),
    ]);
    return Array.from(aliquoteSet)
        .sort((a, b) => a - b)
        .map((aliquota) => {
            const acq = registroAcquisti.filter((e) => e.aliquota === aliquota);
            const vend = registroVendite.filter((e) => e.aliquota === aliquota);
            return {
                aliquota,
                imponibileAcquisti: roundMoney(acq.reduce((s, e) => s + e.imponibile, 0)),
                ivaAcquisti: roundMoney(acq.reduce((s, e) => s + e.iva, 0)),
                imponibileVendite: roundMoney(vend.reduce((s, e) => s + e.imponibile, 0)),
                ivaVendite: roundMoney(vend.reduce((s, e) => s + e.iva, 0)),
            };
        });
};

export const buildPackageTotali = (summaryByAliquota) => {
    const imponibileVendite = roundMoney(summaryByAliquota.reduce((s, r) => s + r.imponibileVendite, 0));
    const ivaVendite = roundMoney(summaryByAliquota.reduce((s, r) => s + r.ivaVendite, 0));
    const imponibileAcquisti = roundMoney(summaryByAliquota.reduce((s, r) => s + r.imponibileAcquisti, 0));
    const ivaAcquisti = roundMoney(summaryByAliquota.reduce((s, r) => s + r.ivaAcquisti, 0));
    return {
        imponibileVendite,
        ivaVendite,
        imponibileAcquisti,
        ivaAcquisti,
        liquidazioneIva: roundMoney(ivaVendite - ivaAcquisti),
    };
};

export const buildAccountantPackage = ({
    quarter,
    year,
    companyDocumentId,
    passiveWorkspace,
    billingWorkspace,
    activeWorkspace,
    complianceWorkspace,
}) => {
    const registroAcquisti = buildRegistroAcquistiEntries(passiveWorkspace, { quarter, year, companyDocumentId });
    const registroVendite = buildRegistroVenditeEntries(billingWorkspace, activeWorkspace, complianceWorkspace, { quarter, year, companyDocumentId });
    const summaryByAliquota = buildSummaryByAliquota(registroAcquisti, registroVendite);
    const totali = buildPackageTotali(summaryByAliquota);
    return {
        id: `pkg-${quarter.toLowerCase()}-${year}-${Date.now()}`,
        companyDocumentId: companyDocumentId || 'aurifon',
        quarter,
        year: Number(year),
        period: `${quarter} ${year}`,
        status: 'pronto',
        generatedAt: dayjs().toISOString(),
        sentAt: null,
        registroAcquisti,
        registroVendite,
        summaryByAliquota,
        totali,
    };
};

export const buildPackageCsvRows = (pkg) => {
    if (!pkg) return '';
    const lines = [
        `# Pacchetto trimestrale – ${pkg.period}`,
        `# Azienda: ${pkg.companyDocumentId} – Generato: ${dayjs(pkg.generatedAt).format('DD/MM/YYYY HH:mm')}`,
        '',
        '## REGISTRO IVA ACQUISTI',
        'Data;Numero;Fornitore;Descrizione;Imponibile;Aliquota %;IVA;Totale',
        ...(pkg.registroAcquisti || []).map((e) =>
            [
                dayjs(e.date).format('DD/MM/YYYY'),
                e.numero,
                e.fornitore,
                e.descrizione,
                e.imponibile.toFixed(2),
                `${e.aliquota}%`,
                e.iva.toFixed(2),
                e.totale.toFixed(2),
            ].join(';')
        ),
        '',
        '## REGISTRO IVA VENDITE',
        'Data;Numero;Cliente;Descrizione;Imponibile;Aliquota %;IVA;Totale',
        ...(pkg.registroVendite || []).map((e) =>
            [
                dayjs(e.date).format('DD/MM/YYYY'),
                e.numero,
                e.cliente,
                e.descrizione,
                e.imponibile.toFixed(2),
                `${e.aliquota}%`,
                e.iva.toFixed(2),
                e.totale.toFixed(2),
            ].join(';')
        ),
        '',
        '## LIQUIDAZIONE IVA',
        'Aliquota %;Imponibile Vendite;IVA Vendite;Imponibile Acquisti;IVA Acquisti',
        ...(pkg.summaryByAliquota || []).map((r) =>
            [
                `${r.aliquota}%`,
                r.imponibileVendite.toFixed(2),
                r.ivaVendite.toFixed(2),
                r.imponibileAcquisti.toFixed(2),
                r.ivaAcquisti.toFixed(2),
            ].join(';')
        ),
        '',
        `Totale IVA vendite;${(pkg.totali?.ivaVendite || 0).toFixed(2)}`,
        `Totale IVA acquisti;${(pkg.totali?.ivaAcquisti || 0).toFixed(2)}`,
        `Liquidazione IVA (a debito/credito);${(pkg.totali?.liquidazioneIva || 0).toFixed(2)}`,
    ];
    return lines.join('\n');
};

const fmtMoney = (value = 0) =>
    `${Math.round((Number(value) + Number.EPSILON) * 100) / 100} €`;

const fmtDate = (date) => (date ? dayjs(date).format('DD/MM/YYYY') : '—');

export const downloadPackagePdf = (pkg, companyLabel) => {
    if (!pkg) return;
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = dayjs().format('DD/MM/YYYY');
    let yPos = 14;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Pacchetto trimestrale – ${pkg.period}`, 14, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (companyLabel) doc.text(`Azienda: ${companyLabel}`, 14, yPos);
    doc.text(`Generato: ${today}`, pageWidth - 14, yPos, { align: 'right' });
    yPos += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Registro IVA Acquisti', 14, yPos);
    yPos += 4;

    doc.autoTable({
        startY: yPos,
        head: [['Data', 'Numero', 'Fornitore', 'Imponibile', 'Al.%', 'IVA', 'Totale']],
        body: (pkg.registroAcquisti || []).map((e) => [
            fmtDate(e.date),
            e.numero,
            e.fornitore,
            fmtMoney(e.imponibile),
            `${e.aliquota}%`,
            fmtMoney(e.iva),
            fmtMoney(e.totale),
        ]),
        theme: 'striped',
        headStyles: { fillColor: [231, 76, 60] },
        styles: { fontSize: 8 },
        columnStyles: {
            3: { halign: 'right' },
            5: { halign: 'right' },
            6: { halign: 'right' },
        },
        margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Registro IVA Vendite', 14, yPos);
    yPos += 4;

    doc.autoTable({
        startY: yPos,
        head: [['Data', 'Numero', 'Cliente', 'Imponibile', 'Al.%', 'IVA', 'Totale']],
        body: (pkg.registroVendite || []).map((e) => [
            fmtDate(e.date),
            e.numero,
            e.cliente,
            fmtMoney(e.imponibile),
            `${e.aliquota}%`,
            fmtMoney(e.iva),
            fmtMoney(e.totale),
        ]),
        theme: 'striped',
        headStyles: { fillColor: [41, 128, 185] },
        styles: { fontSize: 8 },
        columnStyles: {
            3: { halign: 'right' },
            5: { halign: 'right' },
            6: { halign: 'right' },
        },
        margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Liquidazione IVA', 14, yPos);
    yPos += 4;

    doc.autoTable({
        startY: yPos,
        head: [['Aliquota', 'Imponibile Vendite', 'IVA Vendite', 'Imponibile Acquisti', 'IVA Acquisti']],
        body: [
            ...(pkg.summaryByAliquota || []).map((r) => [
                `${r.aliquota}%`,
                fmtMoney(r.imponibileVendite),
                fmtMoney(r.ivaVendite),
                fmtMoney(r.imponibileAcquisti),
                fmtMoney(r.ivaAcquisti),
            ]),
            [
                'TOTALE',
                fmtMoney(pkg.totali?.imponibileVendite),
                fmtMoney(pkg.totali?.ivaVendite),
                fmtMoney(pkg.totali?.imponibileAcquisti),
                fmtMoney(pkg.totali?.ivaAcquisti),
            ],
        ],
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        styles: { fontSize: 8 },
        columnStyles: {
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
        },
        margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 8;

    const liq = pkg.totali?.liquidazioneIva ?? 0;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    const liqLabel = liq >= 0 ? 'IVA a debito (versare)' : 'IVA a credito (riportare)';
    doc.text(`${liqLabel}: ${fmtMoney(Math.abs(liq))}`, 14, yPos);

    const fileName = `pacchetto-trimestrale-${pkg.period.toLowerCase().replace(/\s/g, '-')}-${dayjs().format('YYYY-MM-DD')}.pdf`;
    doc.save(fileName);
};

export const downloadPackageCsv = (pkg) => {
    if (!pkg) return;
    const content = buildPackageCsvRows(pkg);
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
    const fileName = `pacchetto-trimestrale-${pkg.period.toLowerCase().replace(/\s/g, '-')}-${dayjs().format('YYYY-MM-DD')}.csv`;
    saveAs(blob, fileName);
};
